import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_WALLET_HISTORY } from '../graphql/queries';

const LIMIT = 15;

const STATUS_STYLES = {
  success: { className: 'badge badge--success', label: 'Success' },
  pending: { className: 'badge badge--warning', label: 'Pending' },
  blocked: { className: 'badge badge--orange', label: 'Blocked' },
  failed: { className: 'badge badge--danger', label: 'Failed' },
  refunded: { className: 'badge badge--info', label: 'Refunded' },
};

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || { className: 'badge badge--default', label: status };
  return <span className={style.className}>{style.label}</span>;
}

function Pagination({ page, totalPages, onPrev, onNext }) {
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button className="pagination-btn" onClick={onPrev} disabled={page <= 1}>
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Prev
      </button>
      <span className="pagination-info">Page {page} of {totalPages}</span>
      <button className="pagination-btn" onClick={onNext} disabled={page >= totalPages}>
        Next
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(Number(ts));
  return isNaN(d.getTime()) ? ts : d.toLocaleString();
}

export default function WalletHistory() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const offset = (page - 1) * LIMIT;

  const { data, loading } = useQuery(GET_WALLET_HISTORY, {
    variables: { limit: LIMIT, offset, status: statusFilter || undefined },
    fetchPolicy: 'network-only',
  });

  const result = data?.getWalletHistory;
  const history = result?.history || [];
  const count = result?.count || 0;
  const totalPages = Math.ceil(count / LIMIT);

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  return (
    <div className="staff-page">
      <header className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Credit Usage History</h1>
          <p className="dashboard-subtitle">
            {loading ? 'Loading...' : `${count} record${count !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="header-actions">
          <select className="form-input" value={statusFilter} onChange={handleStatusChange} style={{ width: 'auto' }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="blocked">Blocked</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="staff-card">
          {loading ? (
            <div className="staff-loading">
              <div className="loader-ring" />
            </div>
          ) : history.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 14l2 2 4-4" />
                </svg>
              </div>
              <p className="empty-state-title">No history yet</p>
              <p className="empty-state-desc">Credit usage records will appear here.</p>
            </div>
          ) : (
            <>
              <div className="staff-table-wrap">
                <table className="staff-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Operation</th>
                      <th>Staff</th>
                      <th>Credits Used</th>
                      <th>Before</th>
                      <th>After</th>
                      <th>Blocked</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(h.creationDate)}</td>
                        <td>
                          <span style={{ textTransform: 'replace_all', fontSize: '0.8rem', fontWeight: 500 }}>
                            {h.operationType?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>
                          {h.staffId?.name
                            ? <div className="staff-name-cell">
                                <div className="staff-avatar">{h.staffId.name.charAt(0).toUpperCase()}</div>
                                <span className="staff-name">{h.staffId.name}</span>
                              </div>
                            : <span className="text-placeholder">—</span>
                          }
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {h.creditsUsed ?? '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {h.availableCreditsBeforeOperation ?? '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {h.availableCreditsAfterOperation ?? '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {h.blockedCredits ?? 0}
                        </td>
                        <td><StatusBadge status={h.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="staff-table-footer">
                <span className="pagination-count">Showing {history.length} of {count}</span>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPrev={() => setPage((p) => p - 1)}
                  onNext={() => setPage((p) => p + 1)}
                />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
